import { NavigateFunction } from "react-router-dom";
import { getRequiredActionRoute } from "./staffRequiredAction";
import { isMobileClinicUnsupportedViewport } from "./device";
import { Branch, UserProfile } from "../api/services/clinic/account.types";
import { updateCurrentClinicBranch } from "../api/services/clinic/account";
import realtimeClient from "../realtime/client";

const MOBILE_VISITING_MANAGEMENT_PATH = "/mobile/visiting-management";
const UNSUPPORTED_DEVICE_PATH = "/app/upsupported-device";
const DEFAULT_APP_PATH = "/";
const BRANCH_SELECTION_PATH = "/account/branch-selection";

function resolvePostLoginPath(user: UserProfile): string {
    const role = user?.role;
    if (role === "หน้าจอคนไข้") {
        return "/clinic/staff/patient-terminal/";
    }
    if (role === "หน้าจอคิว") {
        return "/clinic/tv/tv-queue/";
    }

    if (isMobileClinicUnsupportedViewport()) {
        if (role === "แพทย์" || role === "doctor") {
            return MOBILE_VISITING_MANAGEMENT_PATH;
        }
        return UNSUPPORTED_DEVICE_PATH;
    }

    return DEFAULT_APP_PATH;
}

function buildBranchSelectionPath(nextPath: string): string {
    if (nextPath !== DEFAULT_APP_PATH) {
        return `${BRANCH_SELECTION_PATH}?redirect=${encodeURIComponent(nextPath)}`;
    }

    return BRANCH_SELECTION_PATH;
}

export function performAuthRedirect(path: string, navigate: NavigateFunction) {
    if (!path) {
        return;
    }

    // Check if it's a Django-served route (not handled by Vite)
    const isDjangoRoute = path.startsWith("/clinic/") || path.startsWith("/tv/");

    if (isDjangoRoute) {
        const apiBase = import.meta.env.VITE_HTTP_API_URL || "";
        // In development, we need to jump to the backend port (e.g., :8000)
        // In production, apiBase might be relative or same-domain
        if (apiBase.startsWith("http")) {
            try {
                const url = new URL(path, apiBase);
                window.location.href = url.href;
            } catch {
                window.location.href = path;
            }
        } else {
            window.location.href = path;
        }
    } else {
        navigate(path, { replace: true });
    }
}

export async function updateCurrentClinicBranchAndReconnect(branchId: number) {
    const response = await updateCurrentClinicBranch(branchId);
    if (response?.success) {
        realtimeClient.reconnect();
    }
    return response;
}

export async function handleUserCurrentClinicBranch(user: UserProfile, navigate: NavigateFunction): Promise<Branch | null> {
    const role = user.role;
    const requiredActionRoute = getRequiredActionRoute(user?.required_action);
    if (role !== "หน้าจอคนไข้" && role !== "หน้าจอคิว" && requiredActionRoute) {
        performAuthRedirect(requiredActionRoute, navigate);
        return null;
    }

    const branches = user.clinic_branches || [];
    const nextPath = resolvePostLoginPath(user);
    if (nextPath === UNSUPPORTED_DEVICE_PATH) {
        performAuthRedirect(nextPath, navigate);
        return null;
    }

    if (branches.length === 1) {
        try {
            const response = await updateCurrentClinicBranchAndReconnect(branches[0].id);
            if (!response.success) {
                navigate(buildBranchSelectionPath(nextPath), { replace: true });
                return null;
            }
            performAuthRedirect(nextPath, navigate);
            return branches[0];
        } catch (err) {
            navigate(buildBranchSelectionPath(nextPath), { replace: true });
            return null;
        }
    } else {
        navigate(buildBranchSelectionPath(nextPath), { replace: true });
        return null;
    }
}
